import React, { useContext, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LAYERS, T } from "../app/tokens.js";
import { UniversalSearchOpenContext } from "../contexts/UniversalSearchContext.jsx";
import { Tooltip } from "./Tooltip.jsx";
import { useKpiPopoverRow } from "./KpiPopoverRowContext.jsx";
import { useHoverIntent } from "./useHoverIntent.js";

// The compact item owns its own visible explanation/action; it never focuses
// or activates an offscreen expanded tile. The portal escapes strip overflow.
export function CondensedKpiMetric({ label, value, secondary, detail, tooltip, breakdown, onActivate, activationLabel, color, scope, isFiltered, testId }) {
  const [anchor, setAnchor] = useState(null);
  const ref = useRef(null);
  const id = useId();
  const searchOpen = useContext(UniversalSearchOpenContext);
  const row = useKpiPopoverRow();
  const hasInfo = tooltip || breakdown?.length || detail;
  const intent = useHoverIntent({
    enabled: Boolean(hasInfo) && !searchOpen,
    interactive: true,
    onOpen: () => {
      show();
      row?.activate(id);
    },
    onClose: () => row?.leaveTile(id),
  });
  const show = () => {
    if (hasInfo && !searchOpen) {
      const rect = ref.current?.getBoundingClientRect();
      if (rect) {
        setAnchor({ top: rect.bottom + 6, left: Math.max(12, Math.min(rect.left, window.innerWidth - 332)) });
      }
    }
  };
  const dismissIntent = intent.dismiss;
  useEffect(() => {
    const hide = dismissIntent;
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [dismissIntent]);
  const rowActiveId = row?.activeId;
  useEffect(() => {
    if (rowActiveId != null && rowActiveId !== id) dismissIntent();
  }, [rowActiveId, id, dismissIntent]);
  const visible = intent.open && anchor && (!row || row.activeId === id);
  return (
    <div style={{ display: "flex", flex: "0 0 auto", alignItems: "center", borderRight: `1px solid ${T.border}`, position: "relative" }}>
      <button ref={ref} type="button" data-testid={testId}
        aria-label={onActivate ? activationLabel ?? label : undefined}
        aria-describedby={visible ? id : undefined}
        {...intent.triggerProps}
        onClick={onActivate ?? intent.show}
        style={{ display: "inline-flex", alignItems: "baseline", gap: 6, whiteSpace: "nowrap", border: 0, background: "transparent", color: T.dark, padding: "7px 14px", font: "inherit", cursor: onActivate ? "pointer" : hasInfo ? "help" : "default" }}>
        <span data-kpi-label="" style={{ fontSize: T.fsXxs, color: T.mid, fontWeight: T.fwBold, textTransform: "uppercase" }}>{label}</span>
        <strong data-kpi-value="" style={{ fontSize: T.fsSm, color: color ?? T.dark }}>{value}</strong>
        {secondary != null && <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold }}>{secondary}</span>}
        {scope === "responsive" && <span aria-label={isFiltered ? "Filtered view" : "Current view"} style={{ color: isFiltered ? T.blue : T.light, fontSize: T.fsXs }}>{isFiltered ? "· Filtered" : "· in view"}</span>}
      </button>
      {visible && hasInfo && createPortal(
        <Tooltip id={id} data-testid={testId ? `${testId}-tooltip` : undefined}
          {...intent.popupProps}
          style={{ position: "fixed", ...anchor, pointerEvents: "auto", width: 300, maxWidth: "calc(100vw - 24px)", boxSizing: "border-box", whiteSpace: "normal", borderRadius: 10, padding: 12, fontSize: T.fsXs, zIndex: LAYERS.kpiPopover }}>
          {detail && <div>{detail}</div>}
          {tooltip}
          {breakdown?.map((row) => <div key={row.label} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>{row.label}</span><strong>{row.value}</strong></div>)}
        </Tooltip>, document.body)}
    </div>
  );
}
